import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import PublicHeader from '../../../components/ui/PublicHeader';
import PublicFooter from '../../../components/ui/PublicFooter';
const PublicLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { candidateLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Get redirect path from location state (for protected routes)
  const from = location.state?.from?.pathname || '/public/candidate-dashboard';

  // Get success message from registration redirect
  const successMessage = location.state?.message;
  const registrationEmail = location.state?.email;

  const [formData, setFormData] = useState({
    email: registrationEmail || '',
    password: '',
    rememberMe: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.text-red-600');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);
    // Clear any previous errors
    setErrors({});

    try {
      // Attempt login
      const response = await candidateLogin({
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      // Check user type - redirect appropriately
      const user = response.user;

      if (user.user_type === 'candidate') {
        // Candidate logged in - redirect to public area or application tracking
        navigate('/public/candidate-dashboard');
      } else {
        // HR user logged in - redirect to admin dashboard
        navigate('/public/jobs');
      }

    } catch (error) {
      console.error('Login failed:', error);
      // Set error message to display on the form
      let errorMessage = 'Invalid email or password. Please try again.';

      // Check if it's a specific error message from the server
      if (error.message && error.message !== 'API request failed' && error.message !== 'An unexpected error occurred') {
        errorMessage = error.message;
      }

      setErrors({
        submit: errorMessage
      });

      // Scroll to error
      setTimeout(() => {
        const errorElement = document.querySelector('.text-red-600');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PublicHeader />
      <div className=" flex flex-col justify-center py-12 sm:px-6 lg:px-8">


        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon name="LogIn" size={24} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">C-success recruit</h1>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your candidate dashboard and track applications
          </p>
        </div>

        {/* Login Form */}
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {/* Success Message from Registration */}
            {successMessage && (
              <div className="mb-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <Icon name="CheckCircle" size={20} color="#059669" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Registration Successful!
                    </h3>
                    <p className="mt-1 text-sm text-green-700">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {errors.submit && (
              <div className="mb-6 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <Icon name="AlertCircle" size={20} color="#dc2626" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Sign In Failed
                    </h3>
                    <p className="mt-1 text-sm text-red-700">{errors.submit}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`appearance-none block w-full px-3 py-2 border ${errors.email ? 'border-red-300' : 'border-gray-300'
                      } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`appearance-none block w-full px-3 py-2 pr-10 border ${errors.password ? 'border-red-300' : 'border-gray-300'
                      } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon
                      name={showPassword ? "EyeOff" : "Eye"}
                      size={20}
                      color="#6b7280"
                    />
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>

              {/* Remember Me and Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link to="/public/forgot-password" className="text-blue-600 hover:text-blue-500">
                    Forgot your password?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <Button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4"
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </div>

              {/* Registration Link */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/public/register" className="text-blue-600 hover:text-blue-500 font-medium">
                    Create one here
                  </Link>
                </p>
              </div>
            </form>

            {/* Alternative Login Methods */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Other options</span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {/* Browse Jobs Without Account */}
                <Link to="/public/jobs" className="block">
                  <Button className="w-full" variant="outline">
                    <Icon name="Search" size={20} className="mr-2" />
                    Browse Jobs Without Account
                  </Button>
                </Link>

                {/* HR Staff Login */}
                <Link to="/admin" className="block">
                  <Button className="w-full" variant="outline">
                    <Icon name="Users" size={20} className="mr-2" />
                    HR Staff Login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Having trouble signing in? Contact our support team at{' '}
            <a href="mailto:info@c-successconsulting.com" className="text-blue-600 hover:text-blue-500">
              info@c-successconsulting.com
            </a>
          </p>
        </div>
      </div>
      <PublicFooter />
    </div>

  );
};

export default PublicLogin;